import React, {useState, useEffect} from 'react';
import {format, isSameDay, isSameMonth, isSameYear} from 'date-fns';
import styles from './TimeSelector.module.css';

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface TimeSelectorProps {
  dateWithTimeIntervals: DateWithTimeIntervals[];
  selected?: TimeInterval;
  onSelected: (time: TimeInterval) => void;
}

export interface DateWithTimeIntervals {
  date: Date;
  timeIntervals: TimeInterval[];
}

export interface TimeInterval {
  startTime: Date;
  endTime: Date;
  providerId: number;
}

interface CalendarDates {
  date: Date;
  available: boolean;
}

// Helper function to convert string or Date to Date object
const convertToDate = (date: Date | string): Date => {
  return date instanceof Date ? date : new Date(date);
};

// Function to clone a date and reset the time
const cloneDate = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * Fill in missing dates in between to keep them continuous
 * @param dates
 * @returns CalendarDates[] with complete weeks
 */
function fillMissingDates(dates: CalendarDates[]): CalendarDates[] {
  if (dates.length === 0) return dates;

  // Sort the array to ensure dates are in order
  dates.sort((a, b) => convertToDate(a.date).getTime() - convertToDate(b.date).getTime());

  // Get the first and last dates
  const firstDate = cloneDate(dates[0].date);
  const lastDate = cloneDate(dates[dates.length - 1].date);

  // Create a set for fast lookup
  const dateSet = new Set(dates.map(dateObj => cloneDate(dateObj.date).getTime()));

  // Fill in missing dates
  const filledDates: CalendarDates[] = [];
  const currentDate = cloneDate(firstDate);

  // eslint-disable-next-line no-unmodified-loop-condition
  while (currentDate <= lastDate) {
    if (!dateSet.has(currentDate.getTime())) {
      filledDates.push({date: cloneDate(currentDate), available: false});
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Combine original dates with filled dates and sort again
  const allDates = [...dates, ...filledDates];
  allDates.sort((a, b) => convertToDate(a.date).getTime() - convertToDate(b.date).getTime());

  return allDates;
}

/**
 * Fill in previous and future dates of a list to give a full week view on either end
 * @param dates
 * @returns CalendarDates[] with complete weeks
 */
function ensureFullWeeks(dates: CalendarDates[]): CalendarDates[] {
  if (dates.length === 0) return dates;

  dates = fillMissingDates(dates);

  // Get the first and last dates
  const firstDate = cloneDate(dates[0].date);
  const lastDate = cloneDate(dates[dates.length - 1].date);

  // Calculate the Sunday of the current week based on today's date
  const today = new Date();
  const currentWeekSunday = cloneDate(today);
  currentWeekSunday.setDate(currentWeekSunday.getDate() - currentWeekSunday.getDay());

  // Add dates to the beginning until the first date is the Sunday of the current week
  while (firstDate.valueOf() > currentWeekSunday.valueOf()) {
    firstDate.setDate(firstDate.getDate() - 1);
    dates.unshift({date: cloneDate(firstDate), available: false});
  }

  // Add dates to the end until the last date is a Saturday
  while (lastDate.getDay() !== 6) {
    lastDate.setDate(lastDate.getDate() + 1);
    dates.push({date: cloneDate(lastDate), available: false});
  }

  // Ensure at least two full weeks
  const requiredDays = 14; // 2 weeks
  while (dates.length < requiredDays) {
    lastDate.setDate(lastDate.getDate() + 1);
    dates.push({date: cloneDate(lastDate), available: false});
  }

  return dates;
}

function formatMonthHeader(start: Date, end: Date): string {
  const startDate = convertToDate(start);
  const endDate = convertToDate(end);
  if (isSameMonth(startDate, endDate)) {
    // month year
    return format(startDate, 'MMM yyyy');
  } else if (isSameYear(startDate, endDate)) {
    // start month - end month year
    return `${format(startDate, 'MMM')} - ${format(endDate, 'MMM yyyy')}`;
  }

  // start month year to end month year
  return `${format(startDate, 'MMM yyyy')} - ${format(endDate, 'MMM yyyy')}`;
}

const TimeSelector: React.FC<TimeSelectorProps> = ({dateWithTimeIntervals, selected, onSelected}) => {
  // when selected is loaded from zustand, the dates get converted to a string - this ensures that we are using
  // a Date type when working with the start time
  const selectedStart = selected?.startTime ? convertToDate(selected.startTime) : undefined;
  const selectedEnd = selected?.endTime ? convertToDate(selected.endTime) : undefined;

  // Find the first date with available time slots
  const firstAvailableDate = dateWithTimeIntervals.find(d => d.timeIntervals && d.timeIntervals.length > 0);
  const firstDate = firstAvailableDate?.date ? convertToDate(firstAvailableDate.date) : new Date();

  const [selectedDate, setSelectedDate] = useState(selectedStart ?? firstDate);

  // Update selected date when dateWithTimeIntervals loads and there's no selected prop
  useEffect(() => {
    if (!selected && dateWithTimeIntervals.length > 0) {
      const firstAvailable = dateWithTimeIntervals.find(d => d.timeIntervals && d.timeIntervals.length > 0);
      if (firstAvailable) {
        setSelectedDate(convertToDate(firstAvailable.date));
      }
    }
  }, [dateWithTimeIntervals, selected]);

  const selectedIndex =
    dateWithTimeIntervals.findIndex(obj => isSameDay(convertToDate(obj.date), convertToDate(selectedDate))) ?? 0;

  const dates: CalendarDates[] = ensureFullWeeks(
    dateWithTimeIntervals.map(d => {
      return {date: d.date, available: true};
    }),
  );

  return (
    <>
      <div className={styles.calendar}>
        {/* Month */}
        <div className={styles.calendarMonthHeader}>
          {formatMonthHeader(firstDate, dateWithTimeIntervals[dateWithTimeIntervals.length - 1]?.date ?? new Date())}
        </div>
        {/* Week day headers */}
        <div className={styles.calendarWeekDays}>
          {WEEKDAYS_SHORT.map((day: string, index: number) => (
            <div key={index} className={styles.calendarWeekDayText}>
              {day.toUpperCase()}
            </div>
          ))}
        </div>
        {/* Dates */}
        <div className={styles.calendarDates}>
          {dates.map(item => {
            const date = convertToDate(item.date);
            const isSelected = isSameDay(date, selectedDate);
            return (
              <button
                key={item.date.toString()}
                className={styles.calendarDateItem}
                onClick={() => setSelectedDate(date)}
                disabled={!item.available}
              >
                {isSelected ? <div className={styles.calendarDateItemSelected} /> : null}
                <span className={styles.calendarDateText} style={{color: isSelected ? 'white' : undefined}}>
                  {format(date, 'd')}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.dateHeader}>{format(selectedDate, 'EEEE, MMMM d')}</div>
      <div className={styles.timeItemsContainer}>
        {(dateWithTimeIntervals[selectedIndex]?.timeIntervals ?? []).map(item => {
          const isSelected =
            convertToDate(item.startTime).valueOf() === selectedStart?.valueOf() &&
            convertToDate(item.endTime).valueOf() === selectedEnd?.valueOf();
          const startTime = convertToDate(item.startTime);
          const timeString = format(startTime, 'h:mma').toLowerCase().replace('m', '');
          return (
            <button
              key={convertToDate(item.startTime).toString()}
              className={`${styles.timeItem} ${isSelected ? styles.timeItemSelected : ''}`}
              onClick={() => onSelected(item)}
            >
              {timeString}
            </button>
          );
        })}
      </div>
    </>
  );
};

export {TimeSelector};
